import React from "react";
import { Actions, BlockVariant, Speaker } from "src/types";

import Button from "@/components/editor/primitives/button/button-component";
import ImageNew from "@/components/editor/primitives/image/image-component";
import RichText from "@/components/editor/primitives/rich-text";
import {
  DEFAULT_GOLDEN_SPONSORS,
  DEFAULT_SCHEDULES,
} from "@/components/editor/shared/constants";
import { DEFAULT_NAVIGATION_LINKS } from "@/components/editor/shared/navigation";
import StandaloneNavigation from "@/components/editor/themes/crosby/blocks/headers/standalone-navigation/standalone-navigation-component";
import { IMAGE_UPLOAD_TYPES } from "@/features/analytics";
import { conditionalShowList } from "@/hooks/use-widget-deprecation";
import humanizeDateRange from "@/utils/dates/humanizeDateRange";
import getInitialItemsOrDefault from "@/utils/get-initial-items-or-default";

import { VIDEO_AREA_SIZE_SMALL_TYPE } from "../../shared/media/video-with-title-settings";
import { createThemeTemplateFactory, syncNavigationLinks } from "../utils";
import HeaderOption1 from "./blocks/headers/header-option-1/header-option-1-component";
import VideoWithTitle from "./blocks/media/video-with-title/video-with-title-component";
import DefaultSchedule from "./blocks/schedules/default-schedule-component";
import SyncedSchedules from "./blocks/schedules/synced-default-schedule-component";
import {
  DEFAULT_DIVIDER_PICTURE_CTA_TEXT,
  DEFAULT_DIVIDER_PICTURE_DESCRIPTION,
  DEFAULT_DIVIDER_PICTURE_SUBTITLE,
} from "./blocks/sections/divider-picture/constants";
import DividerPicture from "./blocks/sections/divider-picture/divider-picture-component";
import ParagraphItem from "./blocks/sections/paragraph-picture/paragraph-item-component";
import ParagraphPicture from "./blocks/sections/paragraph-picture/paragraph-picture-component";
import { DEFAULT_SPEAKERS } from "./blocks/speakers/constants";
import SpeakerCard from "./blocks/speakers/speaker-card/speaker-card-component";
import Speakers from "./blocks/speakers/speakers-component";
import SyncedSpeakers from "./blocks/speakers/synced-speakers-component";
import SponsorCard from "./blocks/sponsors/sponsor-card/sponsor-card-component";
import Sponsors from "./blocks/sponsors/sponsors-component";
import SyncedSponsors from "./blocks/sponsors/synced-sponsors-component";

export default createThemeTemplateFactory(
  (options) =>
    [
      <StandaloneNavigation key="standalone-navigation" showCta isSticky>
        {DEFAULT_NAVIGATION_LINKS.map(({ label, action, noCustomization }) => {
          return (
            <Button
              isLink
              key={label}
              text={label}
              action={action}
              noCustomization={noCustomization}
            />
          );
        })}
      </StandaloneNavigation>,
      <HeaderOption1
        key="header"
        backgroundImage="/canvas/crosby/images/background.740c04e5.jpg"
        ctaText={humanizeDateRange(
          options.event.time_start,
          options.event.time_end,
          options.event.standard_timezone
        ).join(" - ")}
        heading={options.event.name}
        isLogoShowing
        logoSize={50}
        imageFit="cover"
      />,
      <ParagraphPicture key="paragraph-picture">
        <ImageNew src="/canvas/crosby/images/paragraph-picture-placeholder.c8ff9533.jpg" />
        <ParagraphItem showCTA />
      </ParagraphPicture>,
      <VideoWithTitle key="video" videoAreaSize={VIDEO_AREA_SIZE_SMALL_TYPE} />,
      ...conditionalShowList(BlockVariant.MANUAL, options, [
        <Speakers key="speakers">
          {getInitialItemsOrDefault<Speaker>(
            options.speakers,
            8,
            DEFAULT_SPEAKERS
          ).map((speaker) => (
            <SpeakerCard
              key={speaker.id + speaker.name}
              speaker={speaker}
              isBioAvailable={!!speaker.bio}
            />
          ))}
        </Speakers>,
        <DefaultSchedule
          key="schedule"
          schedules={
            options.schedules.length > 0 ? options.schedules : DEFAULT_SCHEDULES
          }
        />,
        <Sponsors key="sponsors" title="Golden Sponsors" layout="golden">
          {getInitialItemsOrDefault(
            options.sponsors,
            3,
            DEFAULT_GOLDEN_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>,
      ]),
      ...conditionalShowList(BlockVariant.SYNCED, options, [
        <SyncedSpeakers key="synced-speakers" />,
        <SyncedSchedules key="synced-schedule" />,
        <SyncedSponsors
          key="synced-sponsors"
          title="Golden Sponsors"
          layout="golden"
        />,
      ]),
      <DividerPicture key="divider-picture">
        <ImageNew
          src="/canvas/crosby/images/divider-picture-placeholder.39332ac7.jpg"
          blockType={IMAGE_UPLOAD_TYPES.DIVIDER_AND_PICTURE}
        />
        <RichText
          text={`<h3 style='text-align: center;'>${DEFAULT_DIVIDER_PICTURE_SUBTITLE}</h3>`}
        />
        <RichText
          text={`<p style='text-align: center;'>${DEFAULT_DIVIDER_PICTURE_DESCRIPTION}</p>`}
        />
        <Button
          text={DEFAULT_DIVIDER_PICTURE_CTA_TEXT}
          action={{
            kind: Actions.TicketSelector,
          }}
        />
      </DividerPicture>,
    ].filter((e) => Boolean(e)),
  (nodeTrees, query) => syncNavigationLinks(nodeTrees, query)
);
